import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';

import amplitude from 'amplitude-js';
import { toggleModal, toggleSpinner } from 'apps/auth/actions/appAction';
import { updateExtraByName } from 'apps/auth/actions/extraAction';
import { getRequestCheque } from 'apps/auth/actions/historyAction';
import { TableContainer } from 'apps/auth/components/common/components/TableRow/styles';
import { DiskBadge } from 'apps/auth/components/common/DiskBadge/DiskBadge';
import { Row } from 'apps/auth/components/common/Layout/Row';
import { Separator } from 'apps/auth/components/common/Layout/Separator';
import { Panel } from 'apps/auth/components/common/Panel/Panel';
import { DiskBadgeWrapper } from 'apps/auth/components/common/Panel/styles';
import PopupNew from 'apps/auth/components/common/Popup/PopupNew';
import { Text } from 'apps/auth/components/common/Text/Text';
import { getRecipientTypeParam } from 'apps/auth/components/utils/AmplitudeUtils';
import ModalComponent from 'apps/auth/components/utils/ModalComponent';
import { INCOMPLETE_TRANSFER_METHOD_NOT_AVAILABLE_SHOWN, RESUME_TRANSFER_CLICKED } from 'apps/auth/constants/amplitude/AmplitudeEvents';
import { PAYMENT_COUNTRY, PAYOUT_COUNTRY, RECIPIENT_TYPE, TO_PRV_CATEGORY } from 'apps/auth/constants/amplitude/AmplitudeParamName';
import { CATEGORY_TRANSFER_TO_ALIPAY, CATEGORY_TRANSFER_TO_LIBRE, CATEGORY_TRANSFER_TO_WECHAT } from 'apps/auth/constants/Constants';
import { INCOMPLETE_TRANSFER } from 'apps/auth/constants/ExtraNames';
import { AUTH_SEND_INCOMPLETE_PAYMENT_CONTINUE_ACTION, HELP_MOBILE, URL_IMAGES_BANKS } from 'apps/auth/constants/UrlConstants';
import { USA } from 'apps/auth/constants/WellKnownCountries';
import { getIconForDiskBadge, getTableTextColor } from 'apps/auth/helpers/historyUtils';
import { getDDMonEEEHHmmDate, getHistoryTransactionFullDate, resolveArguments, toLowerCase } from 'apps/auth/helpers/utils';
import { useAuth, useCheckMeta, useExtra, useLanguage, usePageMessages } from 'apps/auth/hooks/selectors';
import { GTRestrictionService } from 'apps/auth/services/GTRestrictionService';

import CheckTableRow from './cheque/units/CheckTableRow';
import RecipientDetails from './cheque/units/RecipientDetails';
import IncompletePaymentMorePopup from './popup/IncompletePaymentMorePopup';

const IncompletePaymentCheque = (props) => {
    const { countryId } = useAuth();
    const gtRestrictionService = useRef(null);
    const popupRef = useRef(null);
    const messages = usePageMessages();
    const { resumeTransferButton, chequeDate, blockToRecipient } = messages;
    const { lang } = useLanguage();
    const extra = useExtra();
    const chequeMeta = useCheckMeta();
    const cheque = chequeMeta.cheque;
    const userCountry = extra?.userCountry;
    const amplitudeEventInfo = {
        name: INCOMPLETE_TRANSFER_METHOD_NOT_AVAILABLE_SHOWN,
        params: {
            [PAYOUT_COUNTRY]: cheque?.toCountry?.nameEn,
            [PAYMENT_COUNTRY]: userCountry?.nameEn,
            [TO_PRV_CATEGORY]: cheque?.providerToCategory,
        },
    };

    const withoutIconName =
        (cheque?.toBankAlias && cheque?.providerToCategory !== CATEGORY_TRANSFER_TO_LIBRE) ||
        cheque?.providerToCategory === CATEGORY_TRANSFER_TO_WECHAT ||
        cheque?.providerToCategory === CATEGORY_TRANSFER_TO_ALIPAY;

    useEffect(() => {
        gtRestrictionService.current = new GTRestrictionService(messages, popupRef, amplitudeEventInfo);
        gtRestrictionService.current.setActions(props.toggleModal, props.toggleSpinner);
        gtRestrictionService.current.setExtra(extra);
        gtRestrictionService.current.setCheque(chequeMeta);
        return () => props.updateExtraByName(INCOMPLETE_TRANSFER, false);
    }, []);

    const getAvatar = (chequeForAvatar) => {
        if (chequeForAvatar.providerToCategory === CATEGORY_TRANSFER_TO_WECHAT) {
            return `${URL_IMAGES_BANKS}/wechat.png`;
        } else if (chequeForAvatar.providerToCategory === CATEGORY_TRANSFER_TO_ALIPAY) {
            return `${URL_IMAGES_BANKS}/alipay-big.png`;
        } else if (chequeForAvatar.toBankAlias) {
            return `${URL_IMAGES_BANKS}/${chequeForAvatar.toBankAlias}.png`;
        }
        return null;
    };

    const moreClickHandler = (e) => {
        e.preventDefault();
        props.popupRef.current?.open(
            <IncompletePaymentMorePopup modalRef={props.modalRef} popupRef={props.popupRef} faqUrl={`${HELP_MOBILE}${cheque.faqUrl || ''}`} />,
        );
    };

    const getAmount = (chequeForAmount) => {
        const toCurrency =
            chequeForAmount.amountToCurrency && typeof chequeForAmount.amountToCurrency === 'string' ? chequeForAmount.amountToCurrency.toLowerCase() : '';
        return (
            <>
                <Text size="header" color={'primary'} ellipsis lineThrough={false}>
                    {`${chequeForAmount.amountTo} `}
                    <Text size="header" smallCaps color={'primary'}>
                        {toCurrency}
                    </Text>
                </Text>
            </>
        );
    };

    const onCloseModalClick = (e) => {
        e.preventDefault();
        props.modalRef?.current?.close();
    };

    const getFormattedChequeDate = (userCountry, chequeDate, lang) => {
        if (userCountry === USA) {
            const date = getHistoryTransactionFullDate(chequeDate, lang, userCountry);
            return (
                <>
                    {`${date.formattedDate} `}
                    <Text color={getTableTextColor(cheque)} nowrap>
                        {date.formattedTime}
                    </Text>
                </>
            );
        } else {
            return getDDMonEEEHHmmDate(chequeDate, lang);
        }
    };

    return (
        <>
            <ModalComponent onCloseIconClick={onCloseModalClick} helpAndMore={messages.helpAndMore} isButtonWithIcon={true} onClickMoreIcon={moreClickHandler}>
                <>
                    <Row indent="adaptive-16-24">
                        <DiskBadge
                            iconName={withoutIconName ? null : getIconForDiskBadge(cheque, cheque.providerToCategory, cheque.transactionType) + `-48`}
                            src={getAvatar(cheque)}
                            diskSize="large"
                            isDisabled={false}
                            flagName={toLowerCase(cheque.toBankCountryCode)}
                        />
                    </Row>
                    <Text variant="div" color={'primary'} indent="4px" ellipsis>
                        {resolveArguments(blockToRecipient, cheque.toProviderName)}
                    </Text>
                    <Row indent="adaptive-48-64">
                        <Row indent="adaptive-8-16">{cheque.amountTo && getAmount(cheque)}</Row>
                    </Row>
                    <Separator marginTop="0" marginBottom="16px" />
                    <Panel
                        type="link"
                        isHoverable
                        negativeMargin
                        id={`incomplete_payment_link`}
                        href={AUTH_SEND_INCOMPLETE_PAYMENT_CONTINUE_ACTION}
                        onClick={(e) => {
                            amplitude.getInstance().logEvent(RESUME_TRANSFER_CLICKED, { [RECIPIENT_TYPE]: getRecipientTypeParam(cheque.recipientType) });
                            gtRestrictionService.current.checkChequeRestriction(e, false, true);
                        }}
                    >
                        <DiskBadgeWrapper>
                            <DiskBadge variant="primary" iconName={'arrow-forward'} />
                        </DiskBadgeWrapper>
                        <Text color="primary">{resumeTransferButton}</Text>
                    </Panel>
                    <Separator marginTop="16px" />
                    <TableContainer>
                        <CheckTableRow title={chequeDate} titleColor={'lightGray'} textColor={'primary'} cheque={cheque}>
                            {getFormattedChequeDate(countryId, cheque?.date, lang)}
                        </CheckTableRow>
                    </TableContainer>
                    <RecipientDetails cheque={cheque} />
                </>
            </ModalComponent>
            <PopupNew ref={popupRef} overflowHidden={true} />
        </>
    );
};

export default connect(null, { getRequestCheque, toggleModal, toggleSpinner, updateExtraByName })(IncompletePaymentCheque);
